import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from '@headlessui/react'
import { Button, Text } from '@tremor/react'
import { SparklesIcon, XMarkIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { sanitizeUserInput } from '../../utils/security'

function CharacterizationAIGenerationModal({ isOpen, onClose, onSubmit, loading = false, existingResearchContext = {} }) {
    const { t } = useTranslation()
    const [activeStep, setActiveStep] = useState(0)
    const [formResponses, setFormResponses] = useState({})
    const [submitError, setSubmitError] = useState('')

    // Function to get translated options for multiple choice questions
    const getTranslatedOptions = (englishOptions) => {
        const optionMap = {
            'Yes, clearly defined': t('Research.Caracterizacao.Questions.Options.YesClearlyDefined'),
            'Partially defined': t('Research.Caracterizacao.Questions.Options.PartiallyDefined'),
            'No, needs definition': t('Research.Caracterizacao.Questions.Options.NoNeedsDefinition'),
            'Unsure': t('Research.Caracterizacao.Questions.Options.Unsure'),
            'No exclusion criteria': t('Research.Caracterizacao.Questions.Options.NoExclusionCriteria'),
            'Yes, exact number known': t('Research.Caracterizacao.Questions.Options.YesExactNumberKnown'),
            'Approximately known': t('Research.Caracterizacao.Questions.Options.ApproximatelyKnown'),
            'Unknown but estimable': t('Research.Caracterizacao.Questions.Options.UnknownButEstimable'),
            'Completely unknown': t('Research.Caracterizacao.Questions.Options.CompletelyUnknown'),
            'Complete database exists': t('Research.Caracterizacao.Questions.Options.CompleteDatabaseExists'),
            'Partial database exists': t('Research.Caracterizacao.Questions.Options.PartialDatabaseExists'),
            'No database available': t('Research.Caracterizacao.Questions.Options.NoDatabaseAvailable'),
            'Finite and countable': t('Research.Caracterizacao.Questions.Options.FiniteAndCountable'),
            'Finite but large': t('Research.Caracterizacao.Questions.Options.FiniteButLarge'),
            'Indeterminate/infinite': t('Research.Caracterizacao.Questions.Options.IndeterminateInfinite'),
            'Easily accessible': t('Research.Caracterizacao.Questions.Options.EasilyAccessible'),
            'Moderately accessible': t('Research.Caracterizacao.Questions.Options.ModeratelyAccessible'),
            'Difficult to access': t('Research.Caracterizacao.Questions.Options.DifficultToAccess'),
            'Not accessible': t('Research.Caracterizacao.Questions.Options.NotAccessible'),
            'No restrictions': t('Research.Caracterizacao.Questions.Options.NoRestrictions'),
            'Minor restrictions': t('Research.Caracterizacao.Questions.Options.MinorRestrictions'),
            'Significant restrictions': t('Research.Caracterizacao.Questions.Options.SignificantRestrictions'),
            'Major restrictions': t('Research.Caracterizacao.Questions.Options.MajorRestrictions'),
            'Entire population needed': t('Research.Caracterizacao.Questions.Options.EntirePopulationNeeded'),
            'Representative sample sufficient': t('Research.Caracterizacao.Questions.Options.RepresentativeSampleSufficient'),
            'Convenience sample acceptable': t('Research.Caracterizacao.Questions.Options.ConvenienceSampleAcceptable'),
            'Yes, statistical inference required': t('Research.Caracterizacao.Questions.Options.YesStatisticalInferenceRequired'),
            'Partially - some inference needed': t('Research.Caracterizacao.Questions.Options.PartiallySomeInferenceNeeded'),
            'No, descriptive only': t('Research.Caracterizacao.Questions.Options.NoDescriptiveOnly'),
            'Yes, qualitative exploration': t('Research.Caracterizacao.Questions.Options.YesQualitativeExploration'),
            'Mixed qualitative and quantitative': t('Research.Caracterizacao.Questions.Options.MixedQualitativeAndQuantitative'),
            'No, quantitative focus': t('Research.Caracterizacao.Questions.Options.NoQuantitativeFocus'),
            'Complete list available': t('Research.Caracterizacao.Questions.Options.CompleteListAvailable'),
            'Partial list available': t('Research.Caracterizacao.Questions.Options.PartialListAvailable'),
            'No list available': t('Research.Caracterizacao.Questions.Options.NoListAvailable'),
            'Yes, equal probability essential': t('Research.Caracterizacao.Questions.Options.YesEqualProbabilityEssential'),
            'Preferred but not essential': t('Research.Caracterizacao.Questions.Options.PreferredButNotEssential'),
            'Not necessary': t('Research.Caracterizacao.Questions.Options.NotNecessary'),
            'Statistical representativeness essential': t('Research.Caracterizacao.Questions.Options.StatisticalRepresentativenessEssential'),
            'Yes, proportional representation needed': t('Research.Caracterizacao.Questions.Options.YesProportionalRepresentationNeeded'),
            'Partially - some subgroups': t('Research.Caracterizacao.Questions.Options.PartiallySubgroups'),
            'No, overall sample sufficient': t('Research.Caracterizacao.Questions.Options.NoOverallSampleSufficient'),
            'Yes, specific characteristics essential': t('Research.Caracterizacao.Questions.Options.YesSpecificCharacteristicsEssential'),
            'Some specific characteristics needed': t('Research.Caracterizacao.Questions.Options.SomeSpecificCharacteristicsNeeded'),
            'General population acceptable': t('Research.Caracterizacao.Questions.Options.GeneralPopulationAcceptable'),
            'Yes, multivariate analysis planned': t('Research.Caracterizacao.Questions.Options.YesMultivariateAnalysisPlanned'),
            'Some multivariate analysis': t('Research.Caracterizacao.Questions.Options.SomeMultivariateAnalysis'),
            'Simple statistical analysis only': t('Research.Caracterizacao.Questions.Options.SimpleStatisticalAnalysisOnly'),
            'No statistical analysis': t('Research.Caracterizacao.Questions.Options.NoStatisticalAnalysis'),
            'Yes, in-depth interviews planned': t('Research.Caracterizacao.Questions.Options.YesInDepthInterviewsPlanned'),
            'Some qualitative methods': t('Research.Caracterizacao.Questions.Options.SomeQualitativeMethods'),
            'Quantitative methods only': t('Research.Caracterizacao.Questions.Options.QuantitativeMethodsOnly'),
            'Strong literature recommendations exist': t('Research.Caracterizacao.Questions.Options.StrongLiteratureRecommendationsExist'),
            'Some guidance available': t('Research.Caracterizacao.Questions.Options.SomeGuidanceAvailable'),
            'Limited guidance available': t('Research.Caracterizacao.Questions.Options.LimitedGuidanceAvailable'),
            'No specific recommendations': t('Research.Caracterizacao.Questions.Options.NoSpecificRecommendations'),
            'Yes, adequately representative': t('Research.Caracterizacao.Questions.Options.YesAdequatelyRepresentative'),
            'Mostly representative': t('Research.Caracterizacao.Questions.Options.MostlyRepresentative'),
            'Partially representative': t('Research.Caracterizacao.Questions.Options.PartiallyRepresentative'),
            'Not representative': t('Research.Caracterizacao.Questions.Options.NotRepresentative'),
            'No selection biases expected': t('Research.Caracterizacao.Questions.Options.NoSelectionBiasesExpected'),
            'Minor biases possible': t('Research.Caracterizacao.Questions.Options.MinorBiasesPossible'),
            'Some biases likely': t('Research.Caracterizacao.Questions.Options.SomeBiasesLikely'),
            'Significant biases expected': t('Research.Caracterizacao.Questions.Options.SignificantBiasesExpected'),
            'No, systematic recruitment': t('Research.Caracterizacao.Questions.Options.NoSystematicRecruitment'),
            'Partially convenience-based': t('Research.Caracterizacao.Questions.Options.PartiallyConvenienceBased'),
            'Mostly convenience-based': t('Research.Caracterizacao.Questions.Options.MostlyConvenienceBased'),
            'Purely convenience sample': t('Research.Caracterizacao.Questions.Options.PurelyConvenienceSample'),
            'No significant factors': t('Research.Caracterizacao.Questions.Options.NoSignificantFactors'),
            'Minor factors present': t('Research.Caracterizacao.Questions.Options.MinorFactorsPresent'),
            'Some factors likely': t('Research.Caracterizacao.Questions.Options.SomeFactorsLikely'),
            'Significant factors expected': t('Research.Caracterizacao.Questions.Options.SignificantFactorsExpected'),
            'Yes, fully generalizable': t('Research.Caracterizacao.Questions.Options.YesFullyGeneralizable'),
            'Mostly generalizable': t('Research.Caracterizacao.Questions.Options.MostlyGeneralizable'),
            'Partially generalizable': t('Research.Caracterizacao.Questions.Options.PartiallyGeneralizable'),
            'Not generalizable': t('Research.Caracterizacao.Questions.Options.NotGeneralizable')
        }
        return englishOptions.map(option => optionMap[option] || option)
    }

    // 6 categories with 5 questions each (30 total)
    const characterizationQuestions = [
        // Category 1: Study Population Definition
        {
            id: 1,
            category: 'Category1',
            type: 'text',
            question: t('Research.Caracterizacao.Questions.Category1.Q1')
        },
        {
            id: 2,
            category: 'Category1', 
            type: 'text',
            question: t('Research.Caracterizacao.Questions.Category1.Q2')
        },
        {
            id: 3,
            category: 'Category1',
            type: 'text',
            question: t('Research.Caracterizacao.Questions.Category1.Q3')
        },
        {
            id: 4,
            category: 'Category1',
            type: 'multiple_choice',
            question: t('Research.Caracterizacao.Questions.Category1.Q4'),
            options: getTranslatedOptions(['Yes, clearly defined', 'Partially defined', 'No, needs definition', 'Unsure'])
        },
        {
            id: 5,
            category: 'Category1',
            type: 'multiple_choice',
            question: t('Research.Caracterizacao.Questions.Category1.Q5'),
            options: getTranslatedOptions(['Yes, clearly defined', 'Partially defined', 'No exclusion criteria', 'Unsure'])
        },
        
        // Category 2: Study Universe Delimitation
        {
            id: 6,
            category: 'Category2',
            type: 'multiple_choice',
            question: t('Research.Caracterizacao.Questions.Category2.Q1'),
            options: getTranslatedOptions(['Yes, exact number known', 'Approximately known', 'Unknown but estimable', 'Completely unknown'])
        },
        {
            id: 7,
            category: 'Category2',
            type: 'multiple_choice',
            question: t('Research.Caracterizacao.Questions.Category2.Q2'),
            options: getTranslatedOptions(['Complete database exists', 'Partial database exists', 'No database available', 'Unsure'])
        },
        {
            id: 8,
            category: 'Category2',
            type: 'multiple_choice',
            question: t('Research.Caracterizacao.Questions.Category2.Q3'),
            options: getTranslatedOptions(['Finite and countable', 'Finite but large', 'Indeterminate/infinite', 'Unsure'])
        },
        {
            id: 9,
            category: 'Category2',
            type: 'multiple_choice',
            question: t('Research.Caracterizacao.Questions.Category2.Q4'),
            options: getTranslatedOptions(['Easily accessible', 'Moderately accessible', 'Difficult to access', 'Not accessible'])
        },
        {
            id: 10,
            category: 'Category2',
            type: 'multiple_choice',
            question: t('Research.Caracterizacao.Questions.Category2.Q5'),
            options: getTranslatedOptions(['No restrictions', 'Minor restrictions', 'Significant restrictions', 'Major restrictions'])
        },
        
        // Category 3: Sample Definition
        {
            id: 11,
            category: 'Category3',
            type: 'multiple_choice',
            question: t('Research.Caracterizacao.Questions.Category3.Q1'),
            options: getTranslatedOptions(['Entire population needed', 'Representative sample sufficient', 'Convenience sample acceptable', 'Unsure'])
        },
        {
            id: 12,
            category: 'Category3',
            type: 'text',
            question: t('Research.Caracterizacao.Questions.Category3.Q2')
        },
        {
            id: 13,
            category: 'Category3',
            type: 'text',
            question: t('Research.Caracterizacao.Questions.Category3.Q3')
        },
        {
            id: 14,
            category: 'Category3',
            type: 'multiple_choice',
            question: t('Research.Caracterizacao.Questions.Category3.Q4'),
            options: getTranslatedOptions(['Yes, statistical inference required', 'Partially - some inference needed', 'No, descriptive only', 'Unsure'])
        },
        {
            id: 15,
            category: 'Category3',
            type: 'multiple_choice',
            question: t('Research.Caracterizacao.Questions.Category3.Q5'),
            options: getTranslatedOptions(['Yes, qualitative exploration', 'Mixed qualitative and quantitative', 'No, quantitative focus', 'Unsure'])
        },
        
        // Category 4: Sampling Type Selection
        {
            id: 16,
            category: 'Category4',
            type: 'multiple_choice',
            question: t('Research.Caracterizacao.Questions.Category4.Q1'),
            options: getTranslatedOptions(['Complete list available', 'Partial list available', 'No list available', 'Unsure'])
        },
        {
            id: 17,
            category: 'Category4',
            type: 'multiple_choice',
            question: t('Research.Caracterizacao.Questions.Category4.Q2'),
            options: getTranslatedOptions(['Yes, equal probability essential', 'Preferred but not essential', 'Not necessary', 'Unsure'])
        },
        {
            id: 18,
            category: 'Category4',
            type: 'multiple_choice',
            question: t('Research.Caracterizacao.Questions.Category4.Q3'),
            options: getTranslatedOptions(['Statistical representativeness essential', 'Preferred but not essential', 'Not necessary', 'Unsure'])
        },
        {
            id: 19,
            category: 'Category4',
            type: 'multiple_choice',
            question: t('Research.Caracterizacao.Questions.Category4.Q4'),
            options: getTranslatedOptions(['Yes, proportional representation needed', 'Partially - some subgroups', 'No, overall sample sufficient', 'Unsure'])
        },
        {
            id: 20,
            category: 'Category4',
            type: 'multiple_choice',
            question: t('Research.Caracterizacao.Questions.Category4.Q5'),
            options: getTranslatedOptions(['Yes, specific characteristics essential', 'Some specific characteristics needed', 'General population acceptable', 'Unsure'])
        },
        
        // Category 5: Sample Size
        {
            id: 21,
            category: 'Category5',
            type: 'text',
            question: t('Research.Caracterizacao.Questions.Category5.Q1')
        },
        {
            id: 22,
            category: 'Category5',
            type: 'text',
            question: t('Research.Caracterizacao.Questions.Category5.Q2')
        },
        {
            id: 23,
            category: 'Category5',
            type: 'multiple_choice',
            question: t('Research.Caracterizacao.Questions.Category5.Q3'),
            options: getTranslatedOptions(['Yes, multivariate analysis planned', 'Some multivariate analysis', 'Simple statistical analysis only', 'No statistical analysis'])
        },
        {
            id: 24,
            category: 'Category5',
            type: 'multiple_choice',
            question: t('Research.Caracterizacao.Questions.Category5.Q4'),
            options: getTranslatedOptions(['Yes, in-depth interviews planned', 'Some qualitative methods', 'Quantitative methods only', 'Unsure'])
        },
        {
            id: 25,
            category: 'Category5',
            type: 'multiple_choice',
            question: t('Research.Caracterizacao.Questions.Category5.Q5'),
            options: getTranslatedOptions(['Strong literature recommendations exist', 'Some guidance available', 'Limited guidance available', 'No specific recommendations'])
        },
        
        // Category 6: Representativeness and Validity
        {
            id: 26,
            category: 'Category6',
            type: 'multiple_choice',
            question: t('Research.Caracterizacao.Questions.Category6.Q1'),
            options: getTranslatedOptions(['Yes, adequately representative', 'Mostly representative', 'Partially representative', 'Not representative'])
        },
        {
            id: 27,
            category: 'Category6',
            type: 'multiple_choice',
            question: t('Research.Caracterizacao.Questions.Category6.Q2'),
            options: getTranslatedOptions(['No selection biases expected', 'Minor biases possible', 'Some biases likely', 'Significant biases expected'])
        },
        {
            id: 28,
            category: 'Category6',
            type: 'multiple_choice',
            question: t('Research.Caracterizacao.Questions.Category6.Q3'),
            options: getTranslatedOptions(['No, systematic recruitment', 'Partially convenience-based', 'Mostly convenience-based', 'Purely convenience sample'])
        },
        {
            id: 29,
            category: 'Category6',
            type: 'multiple_choice',
            question: t('Research.Caracterizacao.Questions.Category6.Q4'),
            options: getTranslatedOptions(['No significant factors', 'Minor factors present', 'Some factors likely', 'Significant factors expected'])
        },
        {
            id: 30,
            category: 'Category6',
            type: 'multiple_choice',
            question: t('Research.Caracterizacao.Questions.Category6.Q5'),
            options: getTranslatedOptions(['Yes, fully generalizable', 'Mostly generalizable', 'Partially generalizable', 'Not generalizable'])
        }
    ]

    const [additionalContext, setAdditionalContext] = useState('')

    const handleResponseChange = (questionId, value) => {
        setFormResponses(prev => ({
            ...prev,
            [questionId]: value
        }))
    }

    const handleNext = () => {
        if (activeStep < characterizationQuestions.length) {
            setActiveStep(prev => prev + 1)
        }
    }

    const handleBack = () => {
        setActiveStep(prev => prev - 1)
    }

    const handleSubmit = async () => {
        try {
            setSubmitError('')

            // Prepare responses for AI analysis
            const responses = characterizationQuestions.map(question => ({
                question_number: question.id,
                question_text: question.question,
                user_response: formResponses[question.id] || '',
                question_type: question.type,
                category: question.category
            }))

            // Add additional context if provided
            if (additionalContext.trim()) {
                responses.push({
                    question_number: 31,
                    question_text: t('Research.Caracterizacao.Questions.AdditionalContext'),
                    user_response: sanitizeUserInput(additionalContext),
                    question_type: 'text',
                    category: 'additional_context'
                })
            }

            // Include existing research context from previous sections
            const requestData = {
                responses,
                existing_context: {
                    research_question: existingResearchContext.research_question || null,
                    objectives: existingResearchContext.objectives || null,
                    hypotheses: existingResearchContext.hypotheses || null,
                    methodology: existingResearchContext.methodology || null
                }
            }

            await onSubmit(requestData)
            onClose()
            resetForm()
        } catch (err) {
            console.error('Failed to generate AI characterization suggestions:', err)
            setSubmitError(err.message || t('Research.Caracterizacao.GenerateError'))
        }
    }

    const resetForm = () => {
        setActiveStep(0)
        setFormResponses({})
        setAdditionalContext('')
        setSubmitError('')
    }

    const handleClose = () => {
        onClose()
        resetForm()
    }

    const isLastStep = activeStep === characterizationQuestions.length
    const canProceed = isLastStep || formResponses[characterizationQuestions[activeStep]?.id]
    const allQuestionsAnswered = characterizationQuestions.every(q => formResponses[q.id])

    const renderQuestion = (question) => {
        if (question.type === 'multiple_choice') {
            return (
                <div className="space-y-4">
                    {question.options.map((option, index) => (
                        <button
                            key={index}
                            onClick={() => handleResponseChange(question.id, option)}
                            disabled={loading}
                            className={`w-full p-6 text-left rounded-lg border-2 transition-all ${
                                formResponses[question.id] === option 
                                    ? 'border-iavisus-100 bg-iavisus-50 text-iavisus-900'
                                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                            }`}
                        >
                            <div className="font-medium text-lg">
                                {option}
                            </div>
                        </button>
                    ))}
                </div>
            )
        }

        return (
            <div>
                <textarea
                    rows={3}
                    value={formResponses[question.id] || ''}
                    onChange={(e) => handleResponseChange(question.id, e.target.value)}
                    placeholder={t('Research.Caracterizacao.Questions.TextPlaceholder')}
                    className="w-full text-lg p-4 bg-transparent border-0 border-b-2 border-gray-300 focus:border-iavisus-100 focus:outline-none focus:ring-0 transition-colors resize-none overflow-hidden"
                    maxLength={500}
                    disabled={loading}
                    style={{
                        minHeight: 'auto',
                        height: 'auto'
                    }}
                    onInput={(e) => {
                        e.target.style.height = 'auto';
                        e.target.style.height = e.target.scrollHeight + 'px';
                    }}
                />
            </div>
        )
    }

    const renderAdditionalContextStep = () => (
        <>
            <h2 className="text-2xl md:text-3xl font-light text-gray-900 mb-8 leading-tight text-left">
                {t('Research.Caracterizacao.Questions.AdditionalContext')}
            </h2>

            <div className="space-y-3">
                <textarea
                    rows={4}
                    value={additionalContext}
                    onChange={(e) => setAdditionalContext(e.target.value)}
                    placeholder={t('Research.Caracterizacao.Questions.AdditionalContextPlaceholder')}
                    className="w-full text-lg p-4 bg-transparent border-0 border-b-2 border-gray-300 focus:border-iavisus-100 focus:outline-none focus:ring-0 transition-colors resize-none overflow-hidden"
                    maxLength={500}
                    style={{
                        minHeight: 'auto',
                        height: 'auto'
                    }}
                    onInput={(e) => {
                        e.target.style.height = 'auto';
                        e.target.style.height = e.target.scrollHeight + 'px';
                    }}
                />
                <Text className="text-gray-500 text-xs">
                    {t('Research.Caracterizacao.Questions.AdditionalContextHelp')}
                </Text>
            </div>
        </>
    )

    return (
        <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
            <div className="fixed inset-0 bg-black bg-opacity-50" aria-hidden="true" />
            <div className="fixed inset-0">
                <Dialog.Panel className="w-full h-full bg-white flex flex-col">
                    <div className="flex items-center justify-between p-6 border-b">
                        <div>
                            <Dialog.Title className="text-lg font-semibold text-gray-900">
                                {t('Research.Caracterizacao.GenerateAI')}
                            </Dialog.Title>
                            <Text className="text-gray-600 mt-1">
                                {t('Research.Caracterizacao.AIGenerationDescription')}
                            </Text>
                        </div>
                        <button
                            onClick={handleClose}
                            disabled={loading}
                            className="text-gray-400 hover:text-gray-500"
                        >
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                    </div>

                    {/* Main scrollable content */}
                    <div className="flex-1 overflow-y-auto">
                        <div className="p-8 max-w-4xl mx-auto h-full flex flex-col">
                            <div className="flex-1 flex flex-col justify-center">
                                <div className="mb-12">
                                    {submitError && (
                                        <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-6">
                                            <Text className="text-red-600 text-sm">{submitError}</Text>
                                        </div>
                                    )}

                                    {isLastStep ? (
                                        renderAdditionalContextStep()
                                    ) : (
                                        <>
                                            <div className="mb-8">
                                                <Text className="text-sm text-gray-500 mb-2">
                                                    {`${activeStep + 1} / ${characterizationQuestions.length} - ${t('Research.Caracterizacao.Questions.' + characterizationQuestions[activeStep].category + '.Title')}`}
                                                </Text>
                                                <h2 className="text-2xl md:text-3xl font-light text-gray-900 leading-tight text-left">
                                                    {characterizationQuestions[activeStep].question}
                                                </h2>
                                            </div>
                                            {renderQuestion(characterizationQuestions[activeStep])}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="border-t p-6">
                        <div className="flex justify-between items-center">
                            {activeStep > 0 ? (
                                <Button
                                    variant="secondary"
                                    onClick={handleBack}
                                    disabled={loading}
                                    icon={ChevronLeftIcon}
                                    className="bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                                >
                                    {t('Common.Back')}
                                </Button>
                            ) : (
                                <Button
                                    variant="secondary"
                                    onClick={handleClose}
                                    disabled={loading}
                                    className="bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                                >
                                    {t('Common.Cancel')}
                                </Button>
                            )}

                            <div className="flex space-x-3">
                                {!isLastStep ? (
                                    <Button
                                        onClick={handleNext}
                                        disabled={!canProceed || loading}
                                        icon={ChevronRightIcon}
                                        iconPosition="right"
                                        className="bg-iavisus-100 text-white hover:bg-iavisus-200 disabled:opacity-50"
                                    >
                                        {t('Common.Next')}
                                    </Button>
                                ) : (
                                    <Button
                                        onClick={handleSubmit}
                                        icon={SparklesIcon}
                                        disabled={!allQuestionsAnswered || loading}
                                        className="bg-iavisus-100 text-white hover:bg-iavisus-200 disabled:opacity-50"
                                    >
                                        {loading ? t('Research.Caracterizacao.Generating') : t('Research.Caracterizacao.GenerateDefinition')}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>
    )
}

export default CharacterizationAIGenerationModal